import Link from 'next/link'
import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { Modal, message } from 'antd'
import { fetchRule, fetchRuleAuthed, RuleDetail, CATEGORY_LABELS, fetchWechatArticle, recordRuleView } from '@/lib/rulesApi'
import { GAME_LABELS } from '@/lib/games'
import { ORG_ID, absUrl, breadcrumbLd, SeoProps } from '@/utils/seo'

type Props = { rule: RuleDetail | null }

export default function RuleView({ rule }: Props) {
  const router = useRouter()
  const [articleHtml, setArticleHtml] = useState<string | null>(null)
  const [loadingArticle, setLoadingArticle] = useState(false)
  const [canEdit, setCanEdit] = useState(false)

  useEffect(() => {
    if (rule) {
      recordRuleView(rule.slug)
      fetchRuleAuthed(rule.slug).then((d) => setCanEdit(!!d?.can_edit)) // 登录且为管理员/作者时显示编辑按钮
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const genArticle = async () => {
    if (!rule) return
    setLoadingArticle(true)
    const res = await fetchWechatArticle(rule.slug)
    setLoadingArticle(false)
    if (res.ok && res.html) setArticleHtml(res.html)
    else message.error('生成失败')
  }
  const copyArticle = async () => {
    if (!articleHtml) return
    await navigator.clipboard.writeText(articleHtml)
    message.success('已复制 HTML，可粘贴到公众号编辑器')
  }

  if (!rule) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-500">规则不存在或未发布</p>
          <Link href="/rules" className="mt-3 inline-block text-gray-600 hover:text-gray-900">
            ← 返回灵缇规则库
          </Link>
        </div>
      </div>
    )
  }

  const pages =
    rule.display_type === 'image_pages' && rule.cdn_base && rule.page_count
      ? Array.from(
          { length: rule.page_count },
          (_, i) => `${rule.cdn_base}page-${String(i + 1).padStart(2, '0')}.jpg`,
        )
      : []

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-3xl px-4 py-8">
          <nav className="text-sm text-gray-400">
            <Link href="/rules" className="text-gray-600 hover:text-gray-900">
              灵缇规则库
            </Link>
            {rule.game ? (
              <>
                {' / '}
                <Link href={`/rules/g/${rule.game}`} className="text-gray-600 hover:text-gray-900">
                  {GAME_LABELS[rule.game] || rule.game}
                </Link>
              </>
            ) : null}
          </nav>
          {/* 「生成公众号文章 / 编辑」是运营发文与维护工具,仅管理员/该规则作者可见,普通读者看到的是干净的规则页 */}
          {canEdit ? (
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={genArticle}
                disabled={loadingArticle}
                className="rounded-lg border-0 bg-brand px-4 py-1.5 text-sm font-semibold text-white shadow-sm shadow-brand/25 transition hover:bg-brand-dark active:scale-95 disabled:opacity-50"
              >
                {loadingArticle ? '生成中…' : '生成公众号文章'}
              </button>
              {rule.display_type === 'html_text' ? (
                <button
                  type="button"
                  onClick={() => router.push(`/rules/${rule.slug}/edit`)}
                  className="rounded-lg border border-brand bg-white px-4 py-1.5 text-sm font-semibold text-brand shadow-sm transition hover:bg-brand/5 active:scale-95"
                >
                  编辑
                </button>
              ) : null}
            </div>
          ) : null}
          <header className="mb-6 mt-3">
            <div className="mb-2 flex items-center gap-2">
              <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                {CATEGORY_LABELS[rule.category]}
              </span>
              {rule.is_authoritative ? (
                <span className="rounded bg-red-50 px-2 py-0.5 text-xs text-red-600">官方</span>
              ) : null}
              {rule.version_label ? <span className="text-xs text-gray-400">{rule.version_label}</span> : null}
              {typeof rule.views === 'number' ? (
                <span className="text-xs text-gray-400">👁 {rule.views + 1} 次查看</span>
              ) : null}
            </div>
            <h1 className="text-2xl font-black text-gray-900 md:text-3xl">{rule.title}</h1>
            {rule.issuer ? <p className="mt-1 text-sm text-gray-400">{rule.issuer}</p> : null}
          </header>

          {rule.display_type === 'file' ? (
            rule.file_kind === 'pdf' ? (
              <iframe
                src={rule.file_url}
                className="h-[80vh] w-full rounded-xl border border-gray-100 bg-white"
                title={rule.title}
              />
            ) : (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={rule.file_url} alt={rule.title} className="w-full rounded-xl border border-gray-100" />
            )
          ) : rule.display_type === 'html_text' ? (
            <article
              className="prose prose-sm max-w-none rounded-2xl bg-white px-5 py-6 shadow-sm md:prose-base md:px-8 md:py-8
                prose-headings:font-bold
                prose-h1:mb-5 prose-h1:text-2xl
                prose-h2:mb-4 prose-h2:mt-10 prose-h2:border-b-2 prose-h2:border-gray-200 prose-h2:pb-2 prose-h2:text-lg prose-h2:text-gray-900
                prose-h3:mb-2 prose-h3:mt-6 prose-h3:border-l-4 prose-h3:border-gray-800 prose-h3:pl-3 prose-h3:text-base prose-h3:text-gray-900
                prose-p:my-2.5 prose-p:leading-[1.9] prose-p:text-gray-700
                prose-strong:text-gray-900
                prose-blockquote:rounded-r-lg prose-blockquote:border-l-4 prose-blockquote:border-gray-300 prose-blockquote:bg-gray-50 prose-blockquote:px-4 prose-blockquote:py-1 prose-blockquote:font-normal prose-blockquote:not-italic prose-blockquote:text-gray-600
                prose-ul:my-3 prose-ol:my-3 prose-li:my-2 prose-li:leading-[1.85] prose-li:marker:text-gray-400 prose-li:marker:font-normal
                prose-table:my-4 prose-table:overflow-hidden prose-table:rounded-lg prose-table:text-sm prose-th:border prose-th:border-gray-200 prose-th:bg-gray-50 prose-th:px-3 prose-th:py-2 prose-th:text-gray-700 prose-td:border prose-td:border-gray-200 prose-td:px-3 prose-td:py-2
                prose-pre:rounded-lg prose-pre:border prose-pre:border-slate-200 prose-pre:bg-slate-50 prose-pre:text-[13px] prose-pre:leading-relaxed prose-pre:text-slate-700
                prose-hr:my-8 prose-hr:border-gray-100"
              dangerouslySetInnerHTML={{ __html: (rule.body_html || '').replace(/^\s*<h1[^>]*>[\s\S]*?<\/h1>/, '') }}
            />
          ) : (
            <div className="space-y-4">
              {pages.map((src, i) => (
                <figure key={i} className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
                  {/* 扫描件用原生 img 懒加载，避免 next/image 对未知尺寸的处理成本 */}
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={src} alt={`${rule.title} 第 ${i + 1} 页`} loading="lazy" className="w-full" />
                  <figcaption className="border-t border-gray-100 bg-gray-50 px-3 py-1.5 text-right text-xs text-gray-400">
                    第 {i + 1} / {rule.page_count} 页
                  </figcaption>
                </figure>
              ))}
            </div>
          )}

          {rule.source_url ? (
            <p className="mt-6 text-xs text-gray-400">
              原始来源：
              <a href={rule.source_url} target="_blank" rel="noopener noreferrer" className="text-gray-600 underline hover:text-gray-900">
                {rule.source_url}
              </a>
            </p>
          ) : null}

          <Modal
            open={!!articleHtml}
            onCancel={() => setArticleHtml(null)}
            onOk={copyArticle}
            okText="复制 HTML"
            cancelText="关闭"
            title="公众号文章 HTML"
            width={720}
          >
            <p className="mb-2 text-xs text-gray-400">
              复制下方 HTML，粘贴到公众号编辑器（图片首次粘贴后由公众号后台自动抓取）。
            </p>
            <textarea
              readOnly
              value={articleHtml || ''}
              className="h-72 w-full rounded border border-gray-200 p-2 font-mono text-xs"
            />
          </Modal>
        </div>
      </div>
    </>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async (ctx) => {
  const slug = ctx.params?.slug as string
  const rule = await fetchRule(slug)
  if (!rule) return { props: { rule } }
  const path = `/rules/${rule.slug}`
  const game = GAME_LABELS[rule.game || ''] || ''
  const seo: SeoProps = {
    title: `${rule.title}全文 · ${game ? `${game}竞赛规则 · ` : ''}灵缇规则库`,
    description: (rule.summary || `${rule.title}全文在线查阅`).slice(0, 160),
    keywords: [rule.title, rule.short_title, game && `${game}竞赛规则`, game && `${game}规则`, rule.issuer, '灵缇规则库'].filter(Boolean).join(','),
    path,
    type: 'article',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'DigitalDocument',
        name: rule.title,
        alternateName: rule.short_title || undefined,
        description: rule.summary || undefined,
        url: absUrl(path),
        inLanguage: 'zh-CN',
        ...(rule.issuer ? { author: { '@type': 'Organization', name: rule.issuer } } : {}),
        ...(rule.year ? { datePublished: String(rule.year) } : {}),
        ...(rule.version_label ? { version: rule.version_label } : {}),
        ...(rule.source_url ? { isBasedOn: rule.source_url } : {}),
        about: game || undefined,
        publisher: { '@id': ORG_ID },
      },
      breadcrumbLd([
        ['首页', '/'],
        ['规则库', '/rules'],
        ...(rule.game ? ([[`${game}规则`, `/rules/g/${rule.game}`]] as [string, string][]) : []),
        [rule.title, path],
      ]),
    ],
  }
  return { props: { rule, seo } }
}
